// components/UserMenu.js
//
// The account menu under the top bar's profile button: who is signed in, the
// way to the account page, the way out. Signing out used to live only on
// /account, so leaving meant a page visit first; now it is one press from any
// page.
//
// Same split as the other two panels on the bar (CartPopover,
// WishlistPopover): MUI keeps the behaviour — Popover for anchoring, the
// focus trap, Escape and click-away; MenuList for arrow-key movement between
// the items — and ob-* owns the surface (orbiee.css, section 9c). The items
// are plain buttons rather than MenuItem so there is no MUI look to undo.
import { useNavigate } from "react-router-dom";
import { Popover, MenuList } from "@mui/material";
import { UserRound, LayoutDashboard, LogOut } from "lucide-react";
import { topbarPaperProps } from "./CartPopover";
import { useSignOut } from "../hooks/useSignOut";
import { isAdmin, getUsername } from "../utils/auth";

/** `id` of the menu, for the profile button's aria-controls. */
export const USER_MENU_ID = "ob-usermenu";

const PAPER_PROPS = topbarPaperProps("ob-usermenu");

/**
 * Who the header names. Shoppers carry `customer_name`; an admin's profile
 * has only `admin_username`; and the profile cookie can be missing while the
 * auth cookies are still good, in which case the sign-in name has to do.
 */
function displayName(profile) {
  return (
    profile?.customer_name ||
    profile?.admin_username ||
    getUsername() ||
    "Signed in"
  );
}

export default function UserMenu({ anchorEl, onClose, profile, avatarSrc }) {
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { signingOut, signOut } = useSignOut();

  const name = displayName(profile);
  // The e-mail is the sign-in name; shown only when it says something the
  // name line does not.
  const email = profile?.username || "";
  const showEmail = Boolean(email) && email !== name;

  // The same destinations the button itself used to navigate to.
  const account = isAdmin()
    ? { label: "Admin panel", path: "/admin", Icon: LayoutDashboard }
    : { label: "Your account", path: "/account", Icon: UserRound };
  const AccountIcon = account.Icon;

  const go = (path) => {
    onClose?.();
    navigate(path);
  };

  // MenuList handles the arrows and Home/End; Tab closes, as a menu should
  // (and as MUI's own Menu does), instead of cycling inside the focus trap.
  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      onClose?.();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={PAPER_PROPS}
    >
      <div className="ob-usermenu-id">
        <img src={avatarSrc} alt="" className="ob-usermenu-avatar" />
        <div className="min-w-0">
          <div className="ob-usermenu-name" title={name}>
            {name}
          </div>
          {showEmail && (
            <div className="ob-usermenu-mail" title={email}>
              {email}
            </div>
          )}
        </div>
      </div>

      {/* MenuList steps only between children that carry a `tabindex`, so
          every item declares one; `autoFocusItem` puts focus on the first
          when the menu opens. */}
      <MenuList
        id={USER_MENU_ID}
        component="div"
        disablePadding
        autoFocusItem={open}
        className="ob-usermenu-list"
        onKeyDown={handleKeyDown}
        aria-label="Account"
      >
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className="ob-usermenu-item"
          onClick={() => go(account.path)}
        >
          <AccountIcon strokeWidth={1.8} aria-hidden="true" />
          <span>{account.label}</span>
        </button>
        <button
          type="button"
          role="menuitem"
          tabIndex={-1}
          className="ob-usermenu-item ob-usermenu-item-danger"
          onClick={signOut}
          disabled={signingOut}
          aria-busy={signingOut || undefined}
        >
          {signingOut ? (
            <span className="ob-spinner" aria-hidden="true" />
          ) : (
            <LogOut strokeWidth={1.8} aria-hidden="true" />
          )}
          <span>{signingOut ? "Signing out…" : "Sign out"}</span>
        </button>
      </MenuList>
    </Popover>
  );
}
